import {
  Typography,
  Box,
  Button,
  // IconButton,
  useBreakpoint,
} from "@avenue-8/sidekick-ui";
import TuneIcon from "@mui/icons-material/Tune";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import { FindListingsFilters } from "./components/FindListingsFilters";
import { Listing } from "./types/Listing";
import { SearchMapView } from "./components/SearchMapView/SearchMapView";
import { useFindListingsSearchLogic } from "@cma/chat-widgets/find-listings-search/useFindListingsSearchLogic";
import { ChatWidgetContainer } from "../styles";
import { ListingList } from "./components/ListingList";
import { convertMapListingToPropertyDto } from "./helpers/convert-listing-to-property-dto";
import { useWidgetsFindListings } from "@cma/shared/queries/useWidgetsFindListings";
import {
  FIND_LISTINGS_FORM_MODEL_DEFAULT_VALUES,
  FindListingsSchema,
} from "./helpers/property-search-filters-form-helpers";
import {
  convertFiltersSchemeToSidekickFindListingsV3,
  convertWidgetFindListingsSearchToFiltersScheme,
} from "./helpers/search-comparable-form-model";
import { IFindListingsSearchWidget } from "./types/FindListingsSearchWidget";
import { PaginationMeta } from "./types/PaginationMeta";
import { TablePagination } from "@shared/components/TablePagination";
import { ResultsSummary } from "./components/ResultsSummary";
import deepEqual from "deep-equal";
import { useUpdateWidgetArtifact } from "@cma/shared/queries/useUpdateWidgetArtifact";
import { ActiveWidgetContext } from "@cma/store/chat-widget.store";
import { IChatWidget } from "../types/ChatWidget";
import { ListingDetailsWidget } from "../listing-details/ListingDetailsWidget";
import { convertListingToSummarizedListing } from "./helpers/convert-listing-to-summarized-listing";
import { usePresentationLogic } from "../cma/components/usePresentationLogic";
import { ChatWidgetNavbar, ChatWidgetNavbarAction } from "../ChatWidgetNavbar";
import { SyncIndicator } from "@cma/shared/components/SyncIndicator";
import {
  convertListingToComparableProperty,
  convertMapListingToComparableProperty,
} from "./helpers/convert-listing-to-comparable-property";
import {
  buildListingMlsKey,
  extractListingMlsKey,
  MlsListingKey,
} from "@cma/shared/helpers/listing-mls-key";
import { convertComparablePropertyToPropertyDto } from "./helpers/convert-property-dto-to-comparable-property";
import { AppliedFiltersChips } from "./components/AppliedFiltersChips";
import { SortBy } from "./components/SortBy";
import { useWidgetsFindMapListings } from "@cma/shared/queries/useWidgetsFindMapListings";
import { useWidgetsGetTerritoryByArea } from "@cma/shared/queries/useWidgetsGetTerritoryByArea";
import { TerritorySearchBarSuggestions } from "./components/TerritorySearchBarSuggestions";
import {
  MapPropertyItemDto,
  TerritoryItem,
} from "@shared/apis/sidekick/generated";
import { useAuth } from "@shared/hooks/useAuth";
import { US_STATES_COORDS } from "./components/SearchMapView/helpers/us-states-coords";

type FindListingsSearchWidgetProps = {
  widget: IFindListingsSearchWidget;
  setChatWidgetContext: (context: ActiveWidgetContext) => void;
  setChatWidget: (widget: IChatWidget) => void;
  onClose?: () => void;
};

const MainContainer = styled.div`
  position: relative;
  animation: fadeUp 300ms ease-in-out;
  @keyframes fadeUp {
    0% {
      opacity: 0;
      transform: translateY(10px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

type ResultsTabContainerProps = {
  $currentView: ViewKey;
  $isMapVisible: boolean;
};
const ResultsTabContainer = styled.div<ResultsTabContainerProps>`
  background-color: ${(props) => props.theme.colors.background};
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  position: relative;
  z-index: 195;
  transition: transform 150ms ease-in-out;
  &:has(.drag-handle:hover):hover {
    cursor: ${(props) =>
      props.$currentView === "map" ? "pointer" : "inherit"};
    transform: ${({ $currentView, $isMapVisible }) =>
      $currentView === "map" && $isMapVisible
        ? "translateY(-0.25rem)"
        : "none"};
  }
`;

const FiltersContainer = styled(Box)`
  padding: 1rem;
`;

const MapButton = styled(Button)`
  position: sticky;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 200;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background-color: ${(p) => p.theme.colors.background};
`;

const FilterIconButtonContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FilterCounterBullet = styled(Typography)`
  position: absolute;
  top: -4px;
  right: -4px;
  background-color: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.white};
  font-size: ${(p) => p.theme.text.fontSizes.xs};
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
`;

const SortByContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
`;

type ViewKey = "map" | "filters" | "results";
const WIDGET_HEIGHT = "100dvh";

export const FindListingsSearchWidgetV2 = (
  props: FindListingsSearchWidgetProps,
) => {
  const { widget, setChatWidgetContext, setChatWidget, onClose } = props;
  const [listingReferenceOpened, setListingReferenceOpened] = useState<{
    mlsId: string;
    mlsSource: string;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [currentView, setCurrentView] = useState<ViewKey>("results");
  const previousViewRef = useRef<ViewKey>("results");
  const [isMapVisible, setIsMapVisible] = useState(false);
  const { user } = useAuth();
  const { content } = widget;
  const isLaptopDown = useBreakpoint("laptop");

  const {
    presentation,
    isSynching,
    handleAddComparableProperty,
    handleRemoveComparableProperty,
    invalidatePresentation,
  } = usePresentationLogic({
    cmaId: widget.cmaId ?? "",
  });

  const handleChangeView = useCallback(
    (view: ViewKey) => {
      setCurrentView(view);
      previousViewRef.current = currentView;
    },
    [currentView],
  );

  const mapDefaultCenter = useMemo(() => {
    if (user.countryState) {
      return US_STATES_COORDS[
        user.countryState as keyof typeof US_STATES_COORDS
      ];
    }
  }, [user.countryState]);

  useLayoutEffect(() => {
    containerRef.current?.scrollTo({ top: 0, behavior: "instant" });
  }, [widget.kind, currentView]);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const scrollTop = containerRef.current.scrollTop || window.scrollY;

      const isMapCovered = scrollTop > 400;
      setIsMapVisible(!isMapCovered);
    };

    handleScroll();

    const scrollContainer = containerRef.current || window;
    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const defaultFormFilters = useMemo(() => {
    return content
      ? convertWidgetFindListingsSearchToFiltersScheme(content)
      : null;
  }, [content]);

  const {
    filters,
    territorySearchValue,
    readableAppliedFilters,
    propertyTypes,
    propertyStatuses,
    isLoadingPropertyTypes,
    handleApplyMapFilters,
    handleApplyFilters,
    handleApplyTerritoryFilters,
    handleChangePage,
    handleRemoveFilter,
  } = useFindListingsSearchLogic({
    initialValues:
      defaultFormFilters ?? FIND_LISTINGS_FORM_MODEL_DEFAULT_VALUES,
  });

  const findListingsInput = useMemo(() => {
    if (!filters) return null;
    return convertFiltersSchemeToSidekickFindListingsV3(filters);
  }, [filters]);

  const territoryAreaQuery = useWidgetsGetTerritoryByArea({
    params:
      findListingsInput &&
      findListingsInput.areaFilter?.type === "NamedAreaFilter"
        ? {
            name: findListingsInput.areaFilter.name,
            nameType: findListingsInput.areaFilter.nameType,
            city: findListingsInput.areaFilter.context?.city,
            stateAbbreviated:
              findListingsInput.areaFilter.context?.stateAbbreviated,
          }
        : null,
  });

  const selectedAreaGeoJson = useMemo(
    () => territoryAreaQuery.data?.geoJson,
    [territoryAreaQuery.data],
  );

  const onChangePage = useCallback(
    (page: number) => {
      containerRef.current?.scrollTo({
        top: 0,
        behavior: "instant",
      });
      handleChangePage({ page });
    },
    [handleChangePage],
  );

  const onApplyFilters = useCallback(
    (data: FindListingsSchema) => {
      handleApplyFilters(data);
      handleChangeView("results");
    },
    [handleApplyFilters, handleChangeView],
  );

  const updateWidgetArtifactMutation = useUpdateWidgetArtifact();

  const {
    isPending: isPendingListings,
    data,
    error,
  } = useWidgetsFindListings({
    filters,
    onSuccess: (_) => {
      if (widget.temporary) return;

      if (deepEqual(defaultFormFilters, filters) || !findListingsInput) {
        return;
      }

      updateWidgetArtifactMutation.mutate({
        artifactId: widget.artifactId,
        input: findListingsInput,
      });
    },
  });

  const isLoading = isPendingListings || isLoadingPropertyTypes;

  const { isLoading: isLoadingMapListings, data: mapListingsData } =
    useWidgetsFindMapListings({
      filtersState: { ...filters, page: 1 },
    });

  // TODO: fix after API return correct type
  const { listings, meta } = useMemo(() => {
    if (!data) {
      return {
        listings: null,
        meta: null,
      };
    }
    return {
      listings: data.items as unknown as Listing[],
      meta: data.meta as PaginationMeta,
    };
  }, [data]);

  const { mapListings, metaMapListings } = useMemo(() => {
    if (!mapListingsData) {
      return {
        mapListings: null,
        metaMapListings: null,
      };
    }
    return {
      mapListings: mapListingsData.items,
      metaMapListings: mapListingsData.meta as PaginationMeta,
    };
  }, [mapListingsData]);

  const comparablePropertiesResult = useMemo(() => {
    if (!mapListings) return [];
    return mapListings.map(convertMapListingToPropertyDto);
  }, [mapListings]);

  const errorMessage = useMemo(() => {
    if (!isLoading && listings && listings.length === 0) {
      return "No listings found with the selected criteria.";
    }
    if (!error) return null;
    const status = (error as any)?.response?.status || 0;
    if (status === 404) {
      return "No listings found with the selected criteria.";
    }
    return "Failed to find listings. Please try again later.";
  }, [isLoading, listings, error]);

  const handleListingClick = useCallback((id: MlsListingKey) => {
    const { mlsId, mlsSource } = extractListingMlsKey(id);
    setListingReferenceOpened({ mlsId, mlsSource });
  }, []);

  const handleListingCheck = useCallback(
    (listing: Listing, checked: boolean) => {
      if (!widget.cmaId) return;

      if (checked) {
        handleAddComparableProperty(
          convertListingToComparableProperty(listing),
        );
      } else {
        handleRemoveComparableProperty(
          convertListingToComparableProperty(listing),
        );
      }
    },
    [handleAddComparableProperty, handleRemoveComparableProperty, widget.cmaId],
  );

  const handleMapListingCheck = useCallback(
    (mapListing: MapPropertyItemDto | undefined, checked: boolean) => {
      if (!widget.cmaId || !mapListing) return;
      if (checked) {
        handleAddComparableProperty(
          convertMapListingToComparableProperty(mapListing),
        );
      } else {
        handleRemoveComparableProperty(
          convertMapListingToComparableProperty(mapListing),
        );
      }
    },
    [handleAddComparableProperty, handleRemoveComparableProperty, widget.cmaId],
  );

  const mapStatus =
    isLoadingMapListings || isLoadingPropertyTypes ? "loading" : "idle";

  const subjectProperty = useMemo(() => {
    if (!presentation?.sourceData.featuredProperty) return undefined;
    return convertComparablePropertyToPropertyDto(
      presentation.sourceData.featuredProperty,
    );
  }, [presentation?.sourceData.featuredProperty]);

  useEffect(() => {
    if (listingReferenceOpened) {
      setChatWidgetContext({
        content: {
          ui: {
            title: "Property Details",
          },
          data: {
            listingReferenceOpened,
          },
        },
      });
      return;
    }

    setChatWidgetContext({
      content: {
        ui: {
          title: widget.title || "Search Results",
          currentView,
        },
        data: {
          arguments: findListingsInput,
          listings: (listings || []).map(convertListingToSummarizedListing),
          meta,
        },
      },
    });
  }, [
    widget,
    meta,
    setChatWidgetContext,
    listingReferenceOpened,
    listings,
    findListingsInput,
    currentView,
  ]);

  const handleCloseWithCma = useCallback(() => {
    invalidatePresentation();
    if (onClose) {
      onClose();
      return;
    }
    setChatWidget({
      kind: "widget-cma-details",
      artifactId: widget.artifactId,
      content: {
        cmaId: widget.cmaId!,
        artifactId: widget.artifactId,
      },
    });
  }, [
    invalidatePresentation,
    setChatWidget,
    widget.artifactId,
    widget.cmaId,
    onClose,
  ]);

  const handleClose = useMemo(() => {
    if (currentView === "filters") {
      return () => handleChangeView(previousViewRef.current);
    }

    if (listingReferenceOpened) {
      return () => setListingReferenceOpened(null);
    }

    if (widget.cmaId) {
      return () => handleCloseWithCma();
    }

    return null;
  }, [
    currentView,
    listingReferenceOpened,
    widget.cmaId,
    handleChangeView,
    handleCloseWithCma,
  ]);

  const handleMapClick = useCallback(() => {
    handleChangeView("map");
    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [handleChangeView]);

  const syncIndicatorIcon = useMemo(() => {
    return widget.cmaId && !isLaptopDown ? (
      <SyncIndicator
        key="sync-indicator"
        isSynching={isSynching}
        updatedAt={presentation?.sourceData.updatedAt}
      />
    ) : null;
  }, [
    isSynching,
    presentation?.sourceData.updatedAt,
    widget.cmaId,
    isLaptopDown,
  ]);

  const handleOpenFilters = useCallback(() => {
    handleChangeView("filters");
  }, [handleChangeView]);

  const filtersIcon = useMemo(() => {
    return (
      <FilterIconButtonContainer>
        <TuneIcon />
        <FilterCounterBullet>
          {readableAppliedFilters.length}
        </FilterCounterBullet>
      </FilterIconButtonContainer>
    );
  }, [readableAppliedFilters.length]);

  const chatWidgetNavbarActions = useMemo(() => {
    const actions: ChatWidgetNavbarAction[] = [];

    if (syncIndicatorIcon) {
      actions.push({
        key: "sync",
        icon: syncIndicatorIcon,
        label: "Synced Status",
        readOnly: true,
      });
    }

    if (!listingReferenceOpened && currentView !== "filters") {
      actions.push({
        key: "filters",
        icon: filtersIcon,
        label: "Filters",
        onClick: handleOpenFilters,
        disabled: !filters,
        grouped: widget.cmaId ? true : false,
      });
    }

    if (handleClose) {
      actions.push({
        key: "close",
        icon: <CloseIcon />,
        label: "Close",
        onClick: handleClose,
        grouped: widget.cmaId ? true : false,
      });
    }

    return actions;
  }, [
    syncIndicatorIcon,
    filtersIcon,
    handleOpenFilters,
    handleClose,
    filters,
    listingReferenceOpened,
    currentView,
    widget.cmaId,
  ]);

  const bootstrapURLKeys = useMemo(
    () => ({
      key: process.env.GOOGLE_MAPS_API_KEY ?? "",
      libraries: ["places", "geometry"],
      id: "find-listings-google-map-view",
    }),
    [],
  );

  const selectedComparableProperties = useMemo(() => {
    return (presentation?.sourceData.comparableProperties || []).map(
      buildListingMlsKey,
    );
  }, [presentation?.sourceData.comparableProperties]);

  const onMapPropertyCheckStable = useMemo(() => {
    if (!widget.cmaId) return undefined;
    return (mlsKey: MlsListingKey, checked: boolean) => {
      const { mlsId, mlsSource } = extractListingMlsKey(mlsKey);
      handleMapListingCheck(
        {
          mlsId,
          mlsSource,
        } as MapPropertyItemDto,
        checked,
      );
    };
  }, [widget.cmaId, handleMapListingCheck]);

  const handleTerritorySuggestionClick = useCallback(
    (territory: TerritoryItem) => {
      handleApplyTerritoryFilters({
        city: territory.type === "city" ? territory.name : territory.city || "",
        state: territory.state || "",
        neighborhood: territory.type === "neighborhood" ? territory.name : "",
        county: territory.type === "county" ? territory.name : "",
        district: territory.type === "district" ? territory.name : "",
        subDistrict: territory.type === "sub-district" ? territory.name : "",
        schoolDistrict:
          territory.type === "school-district" ? territory.name : "",
      });
    },
    [handleApplyTerritoryFilters],
  );

  const mapFilters = useMemo(() => {
    return filters?.mapFilters;
  }, [filters?.mapFilters]);

  const presentationType = useMemo(() => {
    return widget.cmaId ? "cma" : null;
  }, [widget.cmaId]);

  return (
    <ChatWidgetContainer ref={containerRef}>
      {!widget.embedded && (
        <ChatWidgetNavbar
          actions={chatWidgetNavbarActions}
          subtitle={!listingReferenceOpened ? widget.subtitle : undefined}
        >
          <Typography variant="subtitle1">
            {listingReferenceOpened
              ? "Property Details"
              : widget.title || "Search Results"}
          </Typography>
        </ChatWidgetNavbar>
      )}

      {listingReferenceOpened && (
        <ListingDetailsWidget
          widget={{
            kind: "widget-listing-details",
            artifactId: null,
            listingReference: listingReferenceOpened,
            embedded: true,
          }}
        />
      )}

      <MainContainer hidden={!!listingReferenceOpened}>
        <Box
          ref={mapContainerRef}
          sx={{
            height: `calc(${WIDGET_HEIGHT} - 13.5rem)`,
            maxHeight:
              currentView === "map"
                ? `calc(${WIDGET_HEIGHT} - 13.5rem)`
                : isLaptopDown
                  ? "320px"
                  : "380px",
            transition: "max-height 0.2s ease-in-out",
            position: "sticky",
            top: "4rem",
            zIndex: 100,
            width: "100%",
            marginBottom: "-1rem",
            overflow: "hidden",
          }}
          hidden={currentView === "filters"}
          onClick={handleMapClick}
        >
          <SearchMapView
            key={"google-map-view"}
            status={mapStatus}
            mapFilters={mapFilters}
            onPropertyCheck={onMapPropertyCheckStable}
            onPopoverClick={handleListingClick}
            bootstrapURLKeys={bootstrapURLKeys}
            comparableProperties={comparablePropertiesResult}
            subjectProperty={subjectProperty}
            selectedCompsComposedIds={selectedComparableProperties}
            onApplyMapFilters={handleApplyMapFilters}
            presentationType={presentationType}
            onExpandClick={currentView !== "map" ? handleMapClick : undefined}
            totalItems={metaMapListings?.totalItems || 0}
            geoJsonPolygon={selectedAreaGeoJson}
            defaultCenter={mapDefaultCenter}
          />
        </Box>
        <ResultsTabContainer
          role="tabpanel"
          hidden={currentView === "filters"}
          id="tabpanel-results"
          aria-labelledby="tab-results"
          onClick={() => handleChangeView("results")}
          $currentView={currentView}
          $isMapVisible={isMapVisible}
        >
          <Box
            pt={2}
            display={"flex"}
            alignItems={"center"}
            flexDirection={"column"}
            className="drag-handle"
          >
            {currentView === "map" && (
              <ResultsSummary meta={meta} isLoading={isLoading} />
            )}
          </Box>

          <Box mb={2} mt={1} px={2} onClick={(e) => e.stopPropagation()}>
            <TerritorySearchBarSuggestions
              onSuggestionClick={handleTerritorySuggestionClick}
              value={territorySearchValue}
            />
          </Box>

          <Box mb={2} onClick={(e) => e.stopPropagation()}>
            <AppliedFiltersChips
              filters={readableAppliedFilters}
              onRemoveFilter={handleRemoveFilter}
            />
          </Box>

          <SortByContainer>
            <ResultsSummary meta={meta} isLoading={isLoading} />
            <SortBy
              disabled={isLoading}
              onChange={(sort) =>
                handleApplyFilters({
                  ...filters,
                  sort,
                })
              }
              value={filters?.sort}
            />
          </SortByContainer>

          <ListingList
            listings={listings}
            handleListingClick={handleListingClick}
            handleListingCheck={widget.cmaId ? handleListingCheck : undefined}
            isLoading={isLoading}
            errorMessage={errorMessage}
            checkedListings={selectedComparableProperties}
          />

          {meta && (
            <TablePagination
              page={meta.currentPage}
              totalPages={meta.totalPages}
              onChange={onChangePage}
            />
          )}
        </ResultsTabContainer>
        {!!filters && (
          <FiltersContainer
            role="tabpanel"
            hidden={currentView !== "filters"}
            id="tabpanel-filters"
            aria-labelledby="tab-filters"
          >
            <FindListingsFilters
              onApplyFilters={onApplyFilters}
              defaultValues={filters}
              propertyTypes={propertyTypes}
              propertyStatuses={propertyStatuses}
            />
          </FiltersContainer>
        )}
        <MapButton
          endIcon={<MapOutlinedIcon />}
          onClick={handleMapClick}
          size="small"
          variant="outlined"
          sx={{
            display:
              isMapVisible || currentView === "filters" ? "none" : "flex",
          }}
        >
          Map
        </MapButton>
      </MainContainer>
    </ChatWidgetContainer>
  );
};
